import ErrorBoundary from "@/components/common/ErrorBoundary";
import PageHeader from "@/components/common/PageHeader";
import DataTable from "@/components/datatable/components/DataTable";
import { safeDecompress, tableSearchSchema } from "@/components/datatable/hooks/useTableUrlState";
import { mapTableStateToQueryParams } from "@/components/datatable/utils";
import useConfirm from "@/hooks/useConfirm";
import { usersKeys } from "@/query-keys";
import type { UserListItem } from "@/schemas/userSchema";
import { services } from "@/services";
import { createFileRoute } from "@tanstack/react-router";
import { User, UserX } from "lucide-react";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import NewUser from "./_components/NewUser";
import useColumns from "./_hooks/useColumns";
import useUserMutation from "./_hooks/useUserMutation";
import useUsersQuery from "./_hooks/useUsersQuery";

export const Route = createFileRoute("/_private/users/")({
  validateSearch: tableSearchSchema,
  loaderDeps: ({ search }) => ({
    q: search.q,
  }),
  loader: async ({ deps, context: { queryClient } }) => {
    const tableState = safeDecompress(deps.q);
    const requestParams = mapTableStateToQueryParams(tableState);
    return queryClient.ensureQueryData({
      queryKey: usersKeys.lists(deps.q),
      queryFn: () => services.getUsers(requestParams),
    });
  },
  component: RouteComponent,
  errorComponent: ({ error }) => (
    <ErrorBoundary error={error} reset={() => window.location.reload()} />
  ),
});

function RouteComponent() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();
  const loaderData = Route.useLoaderData();

  const columns = useColumns();
  const { t } = useTranslation();

  const { data: paginatedUsers } = useUsersQuery(search.q, loaderData);
  const { deleteUser, toggleActiveStatus, isDeleting, isToggling } = useUserMutation();

  const deleteConfirm = useConfirm({
    title: t("users.confirm.delete.title"),
    description: t("users.confirm.delete.description"),
    isPending: isDeleting,
  });

  const deactivateConfirm = useConfirm({
    title: t("users.confirm.deactivate.title"),
    description: t("users.confirm.deactivate.description"),
    isPending: isToggling,
  });

  const actions = useMemo(
    () => [
      {
        icon: (row: UserListItem) =>
          row.isActive ? (
            <UserX className="h-4 w-4 shrink-0" />
          ) : (
            <User className="h-4 w-4 shrink-0" />
          ),
        label: (row: UserListItem) =>
          row.isActive ? t("common.deactivate") : t("common.activate"),
        handler: (user: UserListItem) =>
          deactivateConfirm.confirm(() => void toggleActiveStatus(user.id)),
      },
    ],
    [t, deactivateConfirm, toggleActiveStatus],
  );

  const handleView = useCallback(
    (user: UserListItem) => navigate({ to: "/users/$userId", params: { userId: user.id } }),
    [navigate],
  );

  const handleDelete = useCallback(
    (user: UserListItem) => deleteConfirm.confirm(() => void deleteUser(user.id)),
    [deleteConfirm, deleteUser],
  );

  return (
    <>
      <PageHeader
        title={t("users.list.title")}
        subtitle={t("users.list.subtitle")}
        action={<NewUser />}
      />
      <DataTable<UserListItem>
        searchPlaceholder={t("users.list.searchPlaceholder")}
        columns={columns}
        data={paginatedUsers?.items}
        nextCursor={paginatedUsers?.nextCursor}
        hasMore={paginatedUsers?.hasMore}
        queryKey={usersKeys.lists(search.q)}
        navigateFrom="/users"
        searchFrom="/_private/users/"
        onView={handleView}
        onDelete={handleDelete}
        actions={actions}
        searchable={true}
      />

      {deleteConfirm.confirmDialog}
      {deactivateConfirm.confirmDialog}
    </>
  );
}
